import { useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { api, unwrap } from '@/lib/api';
import { useLocale } from '@/i18n/locale-provider';
import { useAsync } from '@/lib/use-async';
import { Card, ErrorPanel, Kpi, PageHeader, Spinner, Table, Td } from '@/components/ui';

/** Range options are built inside the component so their labels can be translated. */

export function AnalyticsPage() {
  const { t, n, money, isRtl, field } = useLocale();
  const [days, setDays] = useState(30);
  const RANGES = [
    { label: t.analytics.range7, days: 7 },
    { label: t.analytics.range30, days: 30 },
    { label: t.analytics.range90, days: 90 },
    { label: t.analytics.range365, days: 365 },
  ];
  const range = `from=${new Date(Date.now() - days * 86_400_000).toISOString()}&to=${new Date().toISOString()}`;

  const summary = useAsync(() => unwrap<never>(api.simulator.get(`/analytics/summary?${range}`)), [range]);
  const sales = useAsync(() => unwrap<never>(api.simulator.get(`/analytics/sales?${range}`)), [range]);
  const branches = useAsync(() => unwrap<never>(api.simulator.get(`/analytics/branches?${range}`)), [range]);
  const customers = useAsync(() => unwrap<never>(api.simulator.get(`/analytics/customers?${range}&limit=10`)), [range]);
  const returns = useAsync(() => unwrap<never>(api.simulator.get(`/analytics/returns?${range}`)), [range]);

  const s = summary.data as unknown as {
    sales: { totalSales: number; totalTax: number; totalDiscount: number; averageOrderValue: number; invoiceCount: number };
    orders: { totalOrders: number; cancelledOrders: number; failedOrders: number };
    customers: { active: number; new: number; purchasing: number };
  } | null;

  const series = (sales.data as unknown as { series: Array<{ date: string; total: number; count: number }> } | null)?.series ?? [];
  const branchRows = (branches.data as unknown as { branches: Array<{ code: string; name: string; nameAr: string | null; city: string; cityAr: string | null; invoiceCount: number; totalSales: number; averageOrderValue: number }> } | null)?.branches ?? [];
  const topCustomers = (customers.data as unknown as { topCustomers: Array<{ customerId: string; name: string; nameAr: string | null; customerType: string; city: string; cityAr: string | null; invoiceCount: number; lifetimeValue: number }> } | null)?.topCustomers ?? [];
  const returnReasons = (returns.data as unknown as { byReason: Array<{ reason: string; count: number; refunded: number }> } | null)?.byReason ?? [];

  return (
    <>
      <PageHeader
        title={t.analytics.title}
        subtitle={t.analytics.subtitle}
        actions={
          <div className="flex gap-1 rounded-md border border-mist-300 bg-white p-0.5">
            {RANGES.map((option) => (
              <button
                key={option.days}
                type="button"
                onClick={() => setDays(option.days)}
                aria-pressed={days === option.days}
                className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  days === option.days ? 'bg-deep-500 text-white' : 'text-mist-600 hover:bg-mist-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        }
      />

      {summary.error ? (
        <ErrorPanel error={summary.error} onRetry={summary.reload} title={t.common.errorTitle} />
      ) : summary.loading && !s ? (
        <Spinner />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label={t.dashboard.totalSales} value={s ? money(s.sales.totalSales) : null}
                 hint={t.dashboard.invoicesCount.replace('{count}', n(s?.sales.invoiceCount ?? 0))} />
            <Kpi label={t.analytics.vatCollected} value={s ? money(s.sales.totalTax) : null} />
            <Kpi label={t.analytics.discountsGiven} value={s ? money(s.sales.totalDiscount) : null} />
            <Kpi label={t.dashboard.averageOrderValue} value={s ? money(s.sales.averageOrderValue) : null} />
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label={t.analytics.ordersPlaced} value={s ? n(s.orders.totalOrders) : null} />
            <Kpi label={t.analytics.cancelled} value={s ? n(s.orders.cancelledOrders) : null}
                 tone={s?.orders.cancelledOrders ? 'warn' : 'good'} />
            <Kpi label={t.analytics.paymentFailures} value={s ? n(s.orders.failedOrders) : null}
                 tone={s?.orders.failedOrders ? 'warn' : 'good'} />
            <Kpi label={t.analytics.customersBought} value={s ? n(s.customers.purchasing) : null}
                 hint={t.analytics.ofActive.replace('{count}', n(s?.customers.active ?? 0))} />
          </div>

          <div className="mt-6 grid gap-5">
            <Card title={t.analytics.revenueOverTime}>
              {series.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={series} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E6EB" vertical={false} />
                    <XAxis dataKey="date" reversed={isRtl} tick={{ fontSize: 11, fill: '#9097AC' }} tickLine={false} axisLine={false} />
                    <YAxis
                      orientation={isRtl ? 'right' : 'left'}
                      tick={{ fontSize: 11, fill: '#9097AC' }} tickLine={false} axisLine={false}
                      tickFormatter={(v: number) => (v >= 1000 ? `${n(Math.round(v / 1000))}k` : n(v))}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [name === 'total' ? money(value) : value, name === 'total' ? t.dashboard.revenue : t.analytics.invoicesCol]}
                      contentStyle={{ borderRadius: 8, border: '1px solid #D0D3DC', fontSize: 12 }}
                    />
                    <Line type="monotone" dataKey="total" stroke="#355872" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="count" stroke="#91D5E5" strokeWidth={2} dot={false} yAxisId={0} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-12 text-center text-sm text-mist-500">{t.analytics.noPeriodSales}</p>
              )}
            </Card>

            <div className="grid gap-5 lg:grid-cols-2">
              <Card title={t.analytics.branchPerformance}>
                <Table head={[t.analytics.branch, t.analytics.invoicesCol, t.analytics.sales, t.analytics.avgOrder]} empty={!branchRows.length}>
                  {branchRows.map((branch) => (
                    <tr key={branch.code} className="hover:bg-mist-50">
                      <Td>
                        <p className="font-medium text-mist-800">{field(branch, 'name')}</p>
                        <p className="text-xs text-mist-500"><span className="ltr-embed">{branch.code}</span> · {field(branch, 'city')}</p>
                      </Td>
                      <Td className="tabular text-end text-mist-600">{n(branch.invoiceCount)}</Td>
                      <Td className="tabular text-end font-medium text-deep-600">{money(branch.totalSales)}</Td>
                      <Td className="tabular text-end text-mist-600">{money(branch.averageOrderValue)}</Td>
                    </tr>
                  ))}
                </Table>
              </Card>

              <Card title={t.analytics.returnsByReason}>
                {returnReasons.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={returnReasons.slice(0, 7)} layout="vertical"
                              margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                              reverseStackOrder={isRtl}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E6EB" horizontal={false} />
                      <XAxis type="number" reversed={isRtl} tick={{ fontSize: 11, fill: '#9097AC' }}
                              tickLine={false} axisLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="reason" width={140} orientation={isRtl ? 'right' : 'left'}
                              tick={{ fontSize: 10, fill: '#555C72' }} tickLine={false} axisLine={false} />
                      <Tooltip
                        formatter={(value: number) => [value, t.nav.returns]}
                        contentStyle={{ borderRadius: 8, border: '1px solid #D0D3DC', fontSize: 12 }}
                      />
                      <Bar dataKey="count" fill="#42B2CD" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="py-12 text-center text-sm text-mist-500">{t.analytics.noReturns}</p>
                )}
              </Card>
            </div>

            <Card title={t.analytics.highestValue}>
              <Table head={[t.analytics.customer, t.analytics.type, t.analytics.city, t.analytics.invoicesCol, t.analytics.lifetimeValue]} empty={!topCustomers.length}>
                {topCustomers.map((customer) => (
                  <tr key={customer.customerId} className="hover:bg-mist-50">
                    <Td className="font-medium text-mist-800">{field(customer, 'name')}</Td>
                    <Td className="text-mist-600">{customer.customerType}</Td>
                    <Td className="text-mist-600">{field(customer, 'city')}</Td>
                    <Td className="tabular text-end text-mist-600">{n(customer.invoiceCount)}</Td>
                    <Td className="tabular text-end font-medium text-deep-600">{money(customer.lifetimeValue)}</Td>
                  </tr>
                ))}
              </Table>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
